import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, type Product, type Snippet, type WalletDesignPreview } from '../api.js';
import { savePreviewPayload } from '../components/TicketPreview.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { CopyCodeBlock, Switch } from '../components/ui.js';
import { Topbar } from './AdminLayout.js';
import { useAuth } from '../auth.js';
import { BRAND } from '../branding.js';
import { isAdminLevel } from '../utils/roles.js';
import { isolate } from '../utils/bidi.js';
import { israelLocalInputOf, israelLocalToIso } from '../utils/israel-time.js';

type Form = Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'shortUrl' | 'profileId' | 'ticketCounter'>;

const RU_TO_EN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function slugify(input: string): string {
  return Array.from(input.toLowerCase().trim())
    .map((ch) => (ch in RU_TO_EN ? RU_TO_EN[ch] : ch))
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/** design.md больше этого — почти наверняка с картинками внутри: их дают ссылками. */
const DESIGN_MAX_BYTES = 200_000;

const IMAGE_NAMES: Record<string, string> = {
  logoURL: 'логотип',
  wideLogoURL: 'широкий логотип',
  thumbnailURL: 'миниатюра',
  stripURL: 'полоса',
  backgroundURL: 'фон',
  iconURL: 'иконка',
};
const LABEL_NAMES: Record<string, string> = { ticket: 'Билет', guest: 'Гость', venue: 'Место', date: 'Дата' };

/** Что выпуск карточки понял из design.md: распознанное и пропущенное. */
function WalletDesignSummary({ preview }: { preview: WalletDesignPreview }): JSX.Element {
  // Неожиданный ответ сервера не должен ронять всю карточку продукта.
  const images = Object.keys(preview.images ?? {}).map((k) => IMAGE_NAMES[k] ?? k);
  const labels = Object.entries(preview.labels ?? {}).map(([k, v]) => `${LABEL_NAMES[k] ?? k} → «${v}»`);
  const back = preview.back ?? [];
  const warnings = preview.warnings ?? [];
  const rows: [string, JSX.Element | string][] = [];
  if (preview.color) {
    rows.push([
      'Фон',
      <span key="c" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: preview.color,
            border: '1px solid var(--border)',
          }}
        />
        <span className="mono">{preview.color}</span>
      </span>,
    ]);
  } else if (preview.colorPreset) {
    rows.push(['Фон', `пресет ${preview.colorPreset}`]);
  }
  if (images.length) rows.push(['Картинки', images.join(', ')]);
  if (labels.length) rows.push(['Подписи', labels.join(', ')]);
  if (back.length) rows.push(['Оборот', `${back.length} строк(и)`]);
  return (
    <div
      style={{
        marginTop: 6,
        padding: 10,
        borderRadius: 8,
        background: 'var(--bg-subtle)',
        fontSize: 12,
        display: 'grid',
        gap: 4,
      }}
    >
      {rows.length === 0 && <div style={{ color: 'var(--fg-subtle)' }}>Ничего не распознано.</div>}
      {rows.map(([k, v]) => (
        <div key={k}>
          <span style={{ color: 'var(--fg-subtle)' }}>{k}:</span> {v}
        </div>
      ))}
      {warnings.map((w) => (
        <div key={w} style={{ color: 'var(--danger-fg)' }}>
          {w}
        </div>
      ))}
    </div>
  );
}

const APP_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'https://pay.banditodigital.com';
const DEFAULT_SUCCESS_URL = `${APP_ORIGIN}/paid`;
const DEFAULT_FAIL_URL = `${APP_ORIGIN}/fail`;

const empty: Form = {
  slug: '',
  name: '',
  description: '',
  priceRub: 0,
  priceIls: 0,
  merchant: '',
  receiptName: '',
  allpayInstallmentsEnabled: true,
  allpayMaxInstallments: 12,
  subscriptionEnabled: false,
  sendpulseBookId: '',
  notionDatabaseId: null,
  flowId: null,
  telegramBotId: null,
  successUrl: DEFAULT_SUCCESS_URL,
  failUrl: DEFAULT_FAIL_URL,
  postPurchaseUrl: '',
  postPurchaseLabel: '',
  salesLimit: null,
  isActive: true,
  ticketingEnabled: false,
  ticketPrefix: '',
  ticketPadding: 3,
  ticketStart: 1,
  eventName: '',
  eventStartsAt: null,
  eventEndsAt: null,
  eventVenue: '',
  eventAddress: '',
  eventGeo: '',
  eventColor: '',
  eventUrl: '',
  eventDescription: '',
  walletDesign: '',
  checkinEnabled: false,
};

// Коды ошибок сохранения, которые бывают при работе из другого активного
// профиля (продукт открыт по ссылке): без текста человек видел бы сырой JSON.
/** Минимальная сумма платежа у AllPay — ₪5 (ответ провайдера «Amount must be at least 5 ILS»). */
const MIN_ILS_MINOR = 500;

const SAVE_ERRORS: Record<string, string> = {
  bot_foreign: 'Этот бот из другого профиля. Переключи активный профиль на профиль продукта и выбери бота заново.',
  flow_foreign: 'Этот флоу из другого профиля. Переключи активный профиль на профиль продукта и выбери флоу заново.',
  slug_taken: 'Такой URL уже занят другим продуктом.',
  notion_db_not_allowed: 'Эта база Notion недоступна для профиля продукта.',
};

export function ProductEdit(): JSX.Element {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminLevel(user?.role);
  const [form, setForm] = useState<Form>(empty);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [snippet, setSnippet] = useState<Snippet | null>(null);
  const [books, setBooks] = useState<{ id: string; name: string }[] | null>(null);
  const [notionDbs, setNotionDbs] = useState<{ id: string; name: string }[] | null>(null);
  const [flows, setFlows] = useState<{ id: string; name: string }[] | null>(null);
  const [bots, setBots] = useState<{ id: string; name: string; username: string }[] | null>(null);
  const [integErr, setIntegErr] = useState<{ books?: string; notionDbs?: string }>({});
  const [ticketCounter, setTicketCounter] = useState<number>(0);
  // design.md карточки Wallet: что из него поймёт выпуск карточки — до сохранения.
  const [designPreview, setDesignPreview] = useState<WalletDesignPreview | null>(null);
  const [designErr, setDesignErr] = useState<string | null>(null);
  const [showDesignText, setShowDesignText] = useState(false);
  // Блок «Событие билета» — только при включённом Wallet у активного профиля.
  const [walletOn, setWalletOn] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const update = <K extends keyof Form>(k: K, v: Form[K]): void =>
    setForm((s) => ({ ...s, [k]: v }));

  const [activeProfileId] = useActiveProfileId();
  const [initialProfileId] = useState(activeProfileId);
  useEffect(() => {
    api
      .walletStatus()
      .then((r) => setWalletOn(r.enabled))
      .catch(() => setWalletOn(false));
  }, [activeProfileId]);

  useEffect(() => {
    setDesignErr(null);
    if (!form.walletDesign.trim()) {
      setDesignPreview(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .previewWalletDesign(form.walletDesign)
        .then(setDesignPreview)
        .catch((e: Error) => setDesignErr(e.message));
    }, 300);
    return () => clearTimeout(t);
  }, [form.walletDesign]);

  // Предпросмотр — в новой вкладке: поля формы (и несохранённые) кладём в
  // localStorage, страница /admin/ticket-preview собирает билет по ним.
  const onTicketPreview = (): void => {
    setPreviewErr(null);
    const key = savePreviewPayload({
      eventName: form.eventName,
      eventStartsAt: form.eventStartsAt,
      eventEndsAt: form.eventEndsAt,
      eventVenue: form.eventVenue,
      eventAddress: form.eventAddress,
      eventGeo: form.eventGeo,
      eventColor: form.eventColor,
      eventUrl: form.eventUrl,
      eventDescription: form.eventDescription,
      walletDesign: form.walletDesign,
      ticketPrefix: form.ticketPrefix,
      ticketPadding: form.ticketPadding,
      ticketStart: form.ticketStart,
      ticketCounter,
    });
    if (!key) {
      setPreviewErr('Браузер не дал сохранить данные для предпросмотра — сохраните продукт и попробуйте снова.');
      return;
    }
    window.open(`/admin/ticket-preview?k=${encodeURIComponent(key)}`, '_blank', 'noopener');
  };

  const onDesignFile = (file: File | undefined): void => {
    if (!file) return;
    if (file.size > DESIGN_MAX_BYTES) {
      setDesignErr('Файл больше 200 КБ. Картинки дайте ссылками, а не внутри файла.');
      return;
    }
    file
      .text()
      .then((text) => update('walletDesign', text))
      .catch(() => setDesignErr('Не удалось прочитать файл.'));
  };
  useEffect(() => {
    if (initialProfileId !== null && activeProfileId !== initialProfileId) {
      nav('/admin/products', { replace: true });
    }
  }, [activeProfileId, initialProfileId, nav]);

  useEffect(() => {
    setBooks(null);
    setIntegErr({});
    api
      .listSendpulseBooks()
      .then((list) => {
        setBooks(list);
        if (isNew) {
          const sales = list.find((b) => b.name.trim().toLowerCase() === 'продажи с сайта');
          if (sales) update('sendpulseBookId', sales.id);
        }
      })
      .catch((e: Error) => setIntegErr((s) => ({ ...s, books: e.message })));
  }, [isNew, activeProfileId]);

  // Notion базы заказов — автоподгрузка (как SendPulse), без ручной кнопки.
  useEffect(() => {
    setNotionDbs(null);
    setIntegErr((s) => ({ ...s, notionDbs: undefined }));
    api
      .listNotionDatabases(activeProfileId ?? undefined)
      .then(setNotionDbs)
      .catch((e: Error) => setIntegErr((s) => ({ ...s, notionDbs: e.message })));
  }, [activeProfileId]);

  // Флоу профиля — для селектора «Сценарий после оплаты».
  useEffect(() => {
    setFlows(null);
    api
      .listFlows()
      .then((list) => setFlows(list.map((f) => ({ id: f.id, name: f.name }))))
      .catch(() => setFlows([]));
  }, [activeProfileId]);

  // Боты выдачи билетов профиля — для селектора в блоке нумерации.
  useEffect(() => {
    setBots(null);
    api
      .listTelegramBots()
      .then(setBots)
      .catch(() => setBots([]));
  }, [activeProfileId]);

  useEffect(() => {
    if (isNew) return;
    void api.getProduct(id!).then((p) => {
      setForm({
        slug: p.slug,
        name: p.name,
        description: p.description,
        priceRub: p.priceRub,
        priceIls: p.priceIls,
        merchant: p.merchant ?? '',
        receiptName: p.receiptName ?? '',
        allpayInstallmentsEnabled: p.allpayInstallmentsEnabled ?? false,
        allpayMaxInstallments: p.allpayMaxInstallments ?? 12,
        subscriptionEnabled: p.subscriptionEnabled ?? false,
        sendpulseBookId: p.sendpulseBookId ?? '',
        notionDatabaseId: p.notionDatabaseId ?? null,
        flowId: p.flowId ?? null,
        telegramBotId: p.telegramBotId ?? null,
        successUrl: p.successUrl,
        failUrl: p.failUrl,
        postPurchaseUrl: p.postPurchaseUrl ?? '',
        postPurchaseLabel: p.postPurchaseLabel ?? '',
        salesLimit: p.salesLimit ?? null,
        isActive: p.isActive,
        ticketingEnabled: p.ticketingEnabled ?? false,
        ticketPrefix: p.ticketPrefix ?? '',
        ticketPadding: p.ticketPadding ?? 3,
        ticketStart: p.ticketStart ?? 1,
        eventName: p.eventName ?? '',
        eventStartsAt: p.eventStartsAt ?? null,
        eventEndsAt: p.eventEndsAt ?? null,
        eventVenue: p.eventVenue ?? '',
        eventAddress: p.eventAddress ?? '',
        eventGeo: p.eventGeo ?? '',
        eventColor: p.eventColor ?? '',
        eventUrl: p.eventUrl ?? '',
        eventDescription: p.eventDescription ?? '',
        walletDesign: p.walletDesign ?? '',
        checkinEnabled: p.checkinEnabled ?? false,
      });
      setTicketCounter(p.ticketCounter ?? 0);
      setLoading(false);
    });
    void api
      .getSnippet(id!)
      .then(setSnippet)
      .catch(() => undefined);
  }, [id, isNew]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = {
        ...form,
        priceRub: Number(form.priceRub),
        priceIls: Number(form.priceIls),
        sendpulseBookId: form.sendpulseBookId || null,
        notionDatabaseId: form.notionDatabaseId || null,
        flowId: form.flowId || null,
        telegramBotId: form.telegramBotId || null,
      };
      if (isNew) {
        const p = await api.createProduct(body);
        nav(`/admin/products/${p.id}`);
      } else {
        await api.updateProduct(id!, body);
      }
    } catch (e) {
      const msg = (e as { details?: unknown }).details;
      const code = typeof msg === 'object' && msg !== null ? (msg as { error?: string }).error : undefined;
      setErr(
        (code && SAVE_ERRORS[code]) ?? (typeof msg === 'string' ? msg : JSON.stringify(msg)),
      );
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!id || isNew) return;
    if (!confirm('Удалить продукт?')) return;
    await api.deleteProduct(id);
    nav('/admin/products');
  };

  if (loading) {
    return (
      <>
        <Topbar title="Продукт" />
        <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={isNew ? 'Новый продукт' : <bdi>{form.name || 'Продукт'}</bdi>}
        subtitle={isNew ? undefined : form.slug}
      />
      <form onSubmit={onSubmit} style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--fg-subtle)' }}>
          <Link to="/admin/products" style={{ textDecoration: 'underline' }}>
            ← Все продукты
          </Link>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 380px',
            gap: 20,
            maxWidth: 1080,
          }}
        >
          <div className="stack">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Основное</div>
              </div>
              <div className="card-body stack">
                <div className="field">
                  <label className="field-label">Название *</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((s) => ({
                        ...s,
                        name,
                        slug: slugTouched ? s.slug : slugify(name),
                      }));
                    }}
                    required
                    placeholder="Например, «Вебинар по налогам»"
                  />
                </div>
                <div className="field">
                  <label className="field-label">URL *</label>
                  <div className="field-prefix">
                    <span className="prefix-txt">/p/</span>
                    <input
                      className="input mono"
                      value={form.slug}
                      onChange={(e) => {
                        setSlugTouched(true);
                        update('slug', e.target.value);
                      }}
                      required
                      placeholder="my-product"
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">Описание</label>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    placeholder="Описание продукта"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Название для чека</label>
                  <input
                    className="input"
                    value={form.receiptName}
                    onChange={(e) => update('receiptName', e.target.value)}
                    placeholder={form.name ? `Как название: «${isolate(form.name)}»` : 'Как название продукта'}
                    maxLength={200}
                  />
                  <div className="field-hint">
                    Так позиция записывается у провайдера: в квитанции (кабале) и кабинете AllPay, в чеке
                    YooKassa. Пусто — берётся название продукта. AllPay показывает до 100 символов.
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">Merchant (бренд)</label>
                  <input
                    className="input"
                    value={form.merchant}
                    onChange={(e) => update('merchant', e.target.value)}
                    placeholder={`Можно оставить пустым — по умолчанию «${BRAND.name}»`}
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Цены</div>
                </div>
              </div>
              <div className="card-body stack">
                <div className="grid-2">
                  <div className="field">
                    <label className="field-label">Цена</label>
                    <div className="field-prefix">
                      <span className="prefix-txt">₪</span>
                      <input
                        className="input no-spinner"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={form.priceIls / 100}
                        onChange={(e) => update('priceIls', Math.round(+e.target.value * 100))}
                        required
                      />
                    </div>
                    {form.priceIls > 0 && form.priceIls < MIN_ILS_MINOR && (
                      <div className="field-hint" style={{ color: 'var(--danger-fg)' }}>
                        AllPay не принимает платежи меньше ₪ {MIN_ILS_MINOR / 100}: покупатель увидит
                        ошибку при оплате. Поставь ₪ {MIN_ILS_MINOR / 100} или больше.
                      </div>
                    )}
                  </div>
                  <div className="field">
                    <label className="field-label">Цена в рублях</label>
                    <div className="field-prefix">
                      <span className="prefix-txt">₽</span>
                      <input
                        className="input no-spinner"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={form.priceRub / 100}
                        onChange={(e) => update('priceRub', Math.round(+e.target.value * 100))}
                        required
                      />
                    </div>
                  </div>
                </div>
                <hr className="hr" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Switch
                      checked={form.allpayInstallmentsEnabled}
                      onChange={(v) =>
                        setForm((s) => ({
                          ...s,
                          allpayInstallmentsEnabled: v,
                          // Installments and subscription can't coexist.
                          subscriptionEnabled: v ? false : s.subscriptionEnabled,
                        }))
                      }
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Рассрочка AllPay</div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        Доступна только для израильских карт
                      </div>
                    </div>
                  </div>
                  {form.allpayInstallmentsEnabled && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 14px',
                        background: 'var(--bg-subtle)',
                        border: '1px solid var(--hairline)',
                        borderRadius: 'var(--radius)',
                      }}
                    >
                      <label style={{ fontSize: 13, fontWeight: 500 }}>Максимум платежей</label>
                      <input
                        className="input mono"
                        type="number"
                        min={1}
                        max={12}
                        step={1}
                        value={form.allpayMaxInstallments}
                        onChange={(e) => {
                          const raw = Number.parseInt(e.target.value, 10);
                          const clamped = Number.isFinite(raw)
                            ? Math.min(12, Math.max(1, raw))
                            : 12;
                          update('allpayMaxInstallments', clamped);
                        }}
                        style={{ width: 72, textAlign: 'center' }}
                      />
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--fg-subtle)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        По ₪{' '}
                        {Math.round(
                          form.priceIls / 100 / Math.max(1, form.allpayMaxInstallments),
                        ).toLocaleString('ru-RU')}{' '}
                        в месяц × {form.allpayMaxInstallments}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Switch
                      checked={form.subscriptionEnabled}
                      onChange={(v) =>
                        setForm((s) => ({
                          ...s,
                          subscriptionEnabled: v,
                          // Subscription and installments can't coexist.
                          allpayInstallmentsEnabled: v ? false : s.allpayInstallmentsEnabled,
                        }))
                      }
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Подписка — ежемесячное списание
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        ₪ — ежемесячное авто-списание через AllPay. ₽ — разовый платёж.
                      </div>
                    </div>
                  </div>
                  {form.subscriptionEnabled && (
                    <div
                      style={{
                        padding: '10px 14px',
                        background: 'var(--bg-subtle)',
                        border: '1px solid var(--hairline)',
                        borderRadius: 'var(--radius)',
                        fontSize: 13,
                        color: 'var(--fg-subtle)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      По ₪ {Math.round(form.priceIls / 100).toLocaleString('ru-RU')} в месяц до
                      отмены. Отмена — в разделе «Подписки».
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Редиректы</div>
              </div>
              <div className="card-body stack">
                <div className="field">
                  <label className="field-label">Success URL *</label>
                  <input
                    className="input mono"
                    type="url"
                    value={form.successUrl}
                    onChange={(e) => update('successUrl', e.target.value)}
                    required
                    placeholder={DEFAULT_SUCCESS_URL}
                  />
                </div>
                <div className="field">
                  <label className="field-label">Fail URL *</label>
                  <input
                    className="input mono"
                    type="url"
                    value={form.failUrl}
                    onChange={(e) => update('failUrl', e.target.value)}
                    required
                    placeholder={DEFAULT_FAIL_URL}
                  />
                </div>
                {BRAND.postPurchaseLink && (
                <>
                <div className="field">
                  <label className="field-label">Ссылка после оплаты</label>
                  <input
                    className="input mono"
                    type="url"
                    value={form.postPurchaseUrl}
                    onChange={(e) => update('postPurchaseUrl', e.target.value)}
                    placeholder="https://t.me/+…"
                  />
                  <div className="field-hint">
                    Кнопка на странице «Оплата прошла». Показывается только при подтверждённом
                    платеже — по прямому заходу на /paid ссылку не получить.
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">Текст кнопки после оплаты</label>
                  <input
                    className="input"
                    value={form.postPurchaseLabel}
                    onChange={(e) => update('postPurchaseLabel', e.target.value)}
                    placeholder="Открыть доступ"
                  />
                </div>
                </>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Интеграции</div>
              </div>
              <div className="card-body stack">
                <div className="grid-2">
                  <div className="field">
                    <label className="field-label">SendPulse — адресная книга</label>
                    {books ? (
                      <select
                        className="select"
                        value={form.sendpulseBookId ?? ''}
                        onChange={(e) => update('sendpulseBookId', e.target.value || null)}
                        disabled={!isAdmin}
                        title={!isAdmin ? 'Изменение доступно только администратору' : undefined}
                      >
                        <option value="">— не выбрано —</option>
                        {books.map((b) => (
                          <option key={b.id} value={b.id} dir="auto">
                            {b.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>
                        {integErr.books ? `Недоступно: ${integErr.books}` : 'Загрузка…'}
                      </div>
                    )}
                  </div>

                  <div className="field">
                    <label className="field-label">Notion — база заказов</label>
                    {notionDbs ? (
                      <select
                        className="select"
                        value={form.notionDatabaseId ?? ''}
                        onChange={(e) => update('notionDatabaseId', e.target.value || null)}
                        disabled={!isAdmin}
                      >
                        <option value="">— из профиля —</option>
                        {notionDbs.map((d) => (
                          <option key={d.id} value={d.id} dir="auto">{d.name}</option>
                        ))}
                        {form.notionDatabaseId &&
                          !notionDbs.some((d) => d.id === form.notionDatabaseId) && (
                            <option value={form.notionDatabaseId}>
                              {form.notionDatabaseId} (недоступна)
                            </option>
                          )}
                      </select>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>
                        {integErr.notionDbs ? `Недоступно: ${integErr.notionDbs}` : 'Загрузка…'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="field">
                  <label className="field-label">Сценарий после оплаты (Флоу)</label>
                  <select
                    className="select"
                    value={form.flowId ?? ''}
                    onChange={(e) => update('flowId', e.target.value || null)}
                    disabled={!isAdmin}
                  >
                    <option value="">— по умолчанию (без флоу) —</option>
                    {flows?.map((f) => (
                      <option key={f.id} value={f.id} dir="auto">{f.name}</option>
                    ))}
                    {form.flowId && !flows?.some((f) => f.id === form.flowId) && (
                      <option value={form.flowId}>{form.flowId} (недоступен)</option>
                    )}
                  </select>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 4 }}>
                    Билет, Notion, оператор и книга SendPulse отработают в любом случае. Флоу
                    добавляется после них и отвечает за письмо клиенту: если в нём есть активное
                    письмо, встроенное письмо не отправляется.
                  </div>
                </div>
              </div>
            </div>

            {form.ticketingEnabled && walletOn && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Событие билета</div>
                </div>
                <div className="card-body stack">
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                    Из него собираются картинка билета, карточка Wallet и событие в календаре — кнопки
                    в письме ({'{{ticket_url}} {{wallet_apple_url}} {{wallet_google_url}} {{calendar_url}}'}), на странице после
                    оплаты и в боте. Без названия и начала кнопок нет. Время — израильское.
                  </div>
                  <div className="field">
                    <label className="field-label">Название</label>
                    <input
                      className="input"
                      value={form.eventName}
                      onChange={(e) => update('eventName', e.target.value.slice(0, 120))}
                      placeholder="AI в деле TLV"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div className="field">
                      <label className="field-label">Начало</label>
                      <input
                        className="input"
                        type="datetime-local"
                        value={form.eventStartsAt ? israelLocalInputOf(form.eventStartsAt) : ''}
                        onChange={(e) =>
                          update('eventStartsAt', e.target.value ? israelLocalToIso(e.target.value) : null)
                        }
                      />
                    </div>
                    <div className="field">
                      <label className="field-label">Конец</label>
                      <input
                        className="input"
                        type="datetime-local"
                        value={form.eventEndsAt ? israelLocalInputOf(form.eventEndsAt) : ''}
                        onChange={(e) =>
                          update('eventEndsAt', e.target.value ? israelLocalToIso(e.target.value) : null)
                        }
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Площадка</label>
                    <input
                      className="input"
                      value={form.eventVenue}
                      onChange={(e) => update('eventVenue', e.target.value.slice(0, 120))}
                      placeholder="ZOA House"
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Адрес</label>
                    <input
                      className="input"
                      value={form.eventAddress}
                      onChange={(e) => update('eventAddress', e.target.value.slice(0, 200))}
                      placeholder="Daniel Frisch St 1, Tel Aviv-Yafo"
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Сайт события</label>
                    <input
                      className="input"
                      value={form.eventUrl}
                      onChange={(e) => update('eventUrl', e.target.value.trim())}
                      placeholder="https://aiconftlv.com"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 8 }}>
                    <div className="field">
                      <label className="field-label">Координаты площадки</label>
                      <input
                        className="input mono"
                        value={form.eventGeo}
                        onChange={(e) => update('eventGeo', e.target.value)}
                        placeholder="32.0853, 34.7818"
                      />
                    </div>
                    <div className="field">
                      <label className="field-label">Цвет</label>
                      <input
                        className="input mono"
                        value={form.eventColor}
                        onChange={(e) => update('eventColor', e.target.value.trim().slice(0, 7))}
                        placeholder="#1F4DFF"
                      />
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
                    Координаты — широта и долгота через запятую: рядом с площадкой карточка Wallet всплывает на
                    экране блокировки. Цвет — фон карточки Wallet и акцент картинки билета, пусто — синий.
                    Сайт — ссылка внизу картинки, страниц билета, в Wallet, календаре и боте.
                  </div>

                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Карточка Wallet</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Выпускается сразу после оплаты, если в «Настройках» включён Wallet и вставлен ключ.
                      Название, дата и место — из события выше, имя гостя и номер билета — из оплаты.
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Описание на обороте</label>
                    <textarea
                      className="textarea"
                      rows={3}
                      value={form.eventDescription}
                      maxLength={256}
                      onChange={(e) => update('eventDescription', e.target.value.slice(0, 256))}
                      placeholder="Вечер практических кейсов: как бизнесу работать с AI"
                    />
                    <div className="field-hint">{form.eventDescription.length} / 256 — ограничение Wallet.</div>
                  </div>
                  <div className="field">
                    <label className="field-label">Оформление (design.md)</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
                        {form.walletDesign ? 'Заменить файл' : 'Загрузить design.md'}
                        <input
                          type="file"
                          accept=".md,.markdown,.txt,text/markdown,text/plain"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            onDesignFile(e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {form.walletDesign && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => setShowDesignText((v) => !v)}
                        >
                          {showDesignText ? 'Скрыть текст' : 'Править текст'}
                        </button>
                      )}
                      {form.walletDesign && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            update('walletDesign', '');
                            setShowDesignText(false);
                          }}
                        >
                          Убрать
                        </button>
                      )}
                      <a
                        href="/admin/api/wallet-design/example"
                        download="design.md"
                        className="btn btn-sm btn-ghost"
                      >
                        <Icon name="download" size={12} /> Пример
                      </a>
                    </div>
                    {showDesignText && (
                      <textarea
                        className="textarea mono"
                        rows={12}
                        style={{ marginTop: 6, fontSize: 12 }}
                        value={form.walletDesign}
                        onChange={(e) => update('walletDesign', e.target.value)}
                      />
                    )}
                    {designErr && <div style={{ fontSize: 12, color: 'var(--danger-fg)' }}>{designErr}</div>}
                    {designPreview && <WalletDesignSummary preview={designPreview} />}
                    <div className="field-hint">
                      Разделы «Цвета», «Картинки», «Подписи», «Оборот» — см. пример. Свой цвет и картинки
                      работают на тарифе Pro WalletWallet, на бесплатном карточка выйдет со стандартным
                      оформлением. Цвет текста WalletWallet подбирает сам.
                    </div>
                  </div>
                  <div className="field">
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={onTicketPreview}
                    >
                      <Icon name="external" size={12} /> Предпросмотр билета
                    </button>
                    <div className="field-hint">Откроется в новой вкладке — по текущим полям, в том числе несохранённым.</div>
                    {previewErr && <div style={{ fontSize: 12, color: 'var(--danger-fg)' }}>{previewErr}</div>}
                  </div>
                </div>
              </div>
            )}

            {err && (
              <div
                className="card"
                style={{
                  padding: 14,
                  color: 'var(--danger-fg)',
                  background: 'var(--danger-subtle-bg)',
                }}
              >
                {err}
              </div>
            )}
          </div>

          <div className="stack">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Публикация</div>
              </div>
              <div className="card-body stack">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Активен</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Принимает новые платежи
                    </div>
                  </div>
                  <Switch checked={form.isActive} onChange={(v) => update('isActive', v)} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Ограничить продажи</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Ссылка закроется после N успешных оплат
                    </div>
                  </div>
                  <Switch
                    checked={form.salesLimit !== null}
                    onChange={(v) => update('salesLimit', v ? (form.salesLimit ?? 1) : null)}
                  />
                </div>
                {form.salesLimit !== null && (
                  <div className="field">
                    <label className="field-label">Максимум успешных оплат</label>
                    <input
                      className="input no-spinner"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step="1"
                      value={form.salesLimit}
                      onChange={(e) => {
                        const n = Math.max(1, Math.floor(+e.target.value || 0));
                        update('salesLimit', n);
                      }}
                    />
                  </div>
                )}
                {/* Ticketing */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Выпускать номера билетов</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Каждой оплате присваивается уникальный порядковый номер
                    </div>
                  </div>
                  <Switch
                    checked={form.ticketingEnabled}
                    onChange={(v) => update('ticketingEnabled', v)}
                  />
                </div>
                {form.ticketingEnabled && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: 8 }}>
                      <div className="field">
                        <label className="field-label">Префикс</label>
                        <input
                          className="input mono"
                          value={form.ticketPrefix}
                          onChange={(e) => update('ticketPrefix', e.target.value.slice(0, 20))}
                          placeholder="AID-"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Длина №</label>
                        <input
                          className="input no-spinner"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={6}
                          value={form.ticketPadding}
                          onChange={(e) => {
                            const n = Math.min(6, Math.max(1, Math.floor(+e.target.value || 1)));
                            update('ticketPadding', n);
                          }}
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Старт с</label>
                        <input
                          className="input no-spinner"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          value={form.ticketStart}
                          onChange={(e) => {
                            const n = Math.max(1, Math.floor(+e.target.value || 1));
                            update('ticketStart', n);
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--fg-subtle)', display: 'flex', gap: 16 }}>
                      <span>
                        Пример: <b>{(form.ticketPrefix || '') + String(form.ticketStart).padStart(form.ticketPadding, '0')}</b>
                      </span>
                      {!isNew && (
                        <span>Выдано: <b>{ticketCounter}</b></span>
                      )}
                    </div>
                    <div className="field">
                      <label className="field-label">Билет через Telegram-бот</label>
                      <select
                        className="select"
                        value={form.telegramBotId ?? ''}
                        onChange={(e) => update('telegramBotId', e.target.value || null)}
                        disabled={!isAdmin}
                      >
                        <option value="">— не выдавать через бот —</option>
                        {bots?.map((b) => (
                          <option key={b.id} value={b.id}>
                            {isolate(b.name)}{b.username ? ` (@${b.username})` : ''}
                          </option>
                        ))}
                        {form.telegramBotId && !bots?.some((b) => b.id === form.telegramBotId) && (
                          <option value={form.telegramBotId}>{form.telegramBotId} (недоступен)</option>
                        )}
                      </select>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 4 }}>
                        Получать билеты в Telegram по команде /tickets.
                        {bots && bots.length === 0 && ' Ботов у профиля пока нет — их заводят в настройках профиля.'}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>Билеты — в чек-ине на входе</div>
                        <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                          Включайте у всех волн билетов ивента, который встречаете. Тестовые и прошлые — выключены.
                        </div>
                      </div>
                      <Switch
                        checked={form.checkinEnabled}
                        onChange={(v) => update('checkinEnabled', v)}
                      />
                    </div>

                  </>
                )}

                <hr className="hr" />
                <button
                  type="submit"
                  disabled={busy}
                  className="btn btn-primary"
                  style={{ justifyContent: 'center', width: '100%' }}
                >
                  <Icon name="check" size={14} />
                  {busy ? 'Сохраняем…' : 'Сохранить'}
                </button>
                {!isNew && form.slug && (
                  <a
                    href={`/p/${form.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn"
                    style={{ justifyContent: 'center', width: '100%' }}
                  >
                    <Icon name="external" size={13} />
                    Просмотр страницы
                  </a>
                )}
                {!isNew && (
                  <button
                    type="button"
                    onClick={onDelete}
                    className="btn btn-danger"
                    style={{ justifyContent: 'center', width: '100%' }}
                  >
                    <Icon name="trash" size={13} />
                    Удалить
                  </button>
                )}
              </div>
            </div>

            {!isNew && snippet && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Встраивание</div>
                </div>
                <div className="card-body stack">
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                    HTML-сниппет (вставка в любой сайт)
                  </div>
                  <CopyCodeBlock text={snippet.snippet} />
                  <hr className="hr" />
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Короткая ссылка</div>
                  {snippet.shortUrl ? (
                    <CopyCodeBlock text={snippet.shortUrl} />
                  ) : (
                    <div
                      style={{
                        padding: '10px 12px',
                        border: '1px dashed var(--border)',
                        borderRadius: 'var(--radius)',
                        color: 'var(--fg-subtle)',
                        fontSize: 12.5,
                      }}
                    >
                      Короткая ссылка ещё не создана.
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ justifyContent: 'center' }}
                    onClick={async () => {
                      if (!id) return;
                      try {
                        await api.regenerateShortUrl(id);
                        const fresh = await api.getSnippet(id);
                        setSnippet(fresh);
                      } catch (err) {
                        const e = err as { status?: number; details?: { error?: string } | string };
                        const msg = e.status === 502
                          ? 'Не получилось создать короткую ссылку. Проверь у профиля ShortURL API URL + API Key в разделе «Настройки».'
                          : `Ошибка: ${typeof e.details === 'string' ? e.details : e.details?.error ?? 'unknown'}`;
                        alert(msg);
                      }
                    }}
                  >
                    <Icon name="refresh" size={13} />
                    {snippet.shortUrl ? 'Перегенерировать' : 'Создать короткую ссылку'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  );
}
